import * as React from 'react';
import dynamic from 'next/dynamic';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useAuth } from '@restworld/feature-authentication';

import { LayoutWrapper } from '../../layouts';
import { JobExplorerContent } from './job-explorer-content';
import { useExplorerInitialApiCalls } from './job-explorer.hooks';
import { useExplorerFilterUrlParams } from './job-explorer.hooks';
import { LeadUserOnboardingPrompt } from './lead-user-onboarding-prompt';
import { useFetchWorkerDataQuery } from '../../hooks';
import { hasMapViewAtom } from './job-explorer-list.atoms';
import ExplorerFilters from './job-explorer-filters';
import { cn } from '@restworld/utils-common';

const RestaurantInfoModal = dynamic(() => import('./job-explorer-map/restaurant-info-modal'), {
  ssr: false,
});

export interface JobExplorerDynamicProps {
  metaTitle?: string;
  metaDescription?: string;
  canonicalPath?: string;
}

export const JobExplorer: React.FC<JobExplorerDynamicProps> = ({ metaTitle, metaDescription, canonicalPath }) => {
  const { user, isAuthenticated } = useAuth();
  const hasMapView = useAtomValue(hasMapViewAtom);
  const { t } = useTranslation('job-explorer-page');
  useExplorerFilterUrlParams();
  useExplorerInitialApiCalls();
  useFetchWorkerDataQuery();

  const metaTitleFinal = metaTitle || t('explorer_meta_title');
  const metaDescriptionFinal = metaDescription || t('explorer_meta_description');

  const additionalNavbarContentEl = React.useMemo(
    () => (
      <div className="py-4">
        <ExplorerFilters isExplorerHomePage={false} testId="job-explorer-filters" />
      </div>
    ),
    []
  );

  return (
    <LayoutWrapper
      title={t('search_for_jobs')}
      navbarVariant="worker"
      hasNavbar
      showSearchBarInNavbar={isAuthenticated ? user?.type !== 'employer' : true}
      noHorizontalPadding
      noPaddingBottom
      noPaddingTop
      // A path and not an absolute URL: the layout adds the locale prefix, which
      // the hardcoded Italian canonical used to throw away on /en.
      canonicalPath={canonicalPath}
      seoProps={{
        title: metaTitleFinal,
        description: metaDescriptionFinal,
        openGraph: {
          title: metaTitleFinal,
          description: metaDescriptionFinal,
        },
      }}
      additionalNavbarContentEl={additionalNavbarContentEl}
    >
      <FilterAndListWrapper data-cy="job-explorer-page" hasMapView={hasMapView}>
        <JobExplorerContent />
      </FilterAndListWrapper>
      <RestaurantInfoModal />
      <LeadUserOnboardingPrompt />
    </LayoutWrapper>
  );
};

const FilterAndListWrapper = ({
  hasMapView,
  className,
  ...props
}: { hasMapView?: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'bg-neutral-10 pt-[155px] md:pt-[168px] lg:pt-[177px] pb-[8rem] lg:pb-0 xl:px-0 lg:px-0',
      hasMapView && 'pb-0 overflow-hidden lg:overflow-auto',
      className
    )}
    {...props}
  />
);
